import { CircleCheck, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Issue } from '../../lib/metrics';
import { Card, StatusChip } from '../atoms';

interface IssueListProps {
  issues: Issue[];
  title: string;
  /** Fleet view: name the client and link each row to it. */
  showClient?: boolean;
  /** Shown when there is nothing to list. */
  allClear: string;
  raised?: boolean;
}

const ROW = 'grid grid-cols-[132px_minmax(0,1fr)] items-center gap-x-4 gap-y-1 border-t border-line py-3 sm:grid-cols-[132px_minmax(0,1fr)_76px_150px]';

/** What is wrong right now, one line each: state, condition, current value, how long. */
export function IssueList({ issues, title, showClient = false, allClear, raised = true }: IssueListProps) {
  return (
    <Card as="section" raised={raised} aria-label={title} className="flex flex-col px-6 pb-2 pt-5">
      <div className="mb-3 flex items-center gap-2.5">
        <h2 className="text-xl">{title}</h2>
        {issues.length > 0 && (
          <span className="rounded-full bg-chip-critical-bg px-2.5 py-0.5 text-[13px] font-semibold text-chip-critical-fg">{issues.length}</span>
        )}
      </div>

      {issues.length === 0 ? (
        <p className="flex items-center gap-2 border-t border-line py-4 text-sm text-fg-secondary">
          <CircleCheck size={18} className="text-good" aria-hidden />
          {allClear}
        </p>
      ) : (
        <ul>
          {issues.map((issue) => {
            const body = (
              <>
                <StatusChip tone={issue.tone} label={issue.state} className="justify-self-start" />
                <span className="min-w-0 text-sm">
                  {showClient && <span className="font-semibold text-fg">{issue.clientName} · </span>}
                  <span className="text-fg-secondary">{issue.summary}</span>
                </span>
                <span className="col-start-2 font-mono text-[15px] font-medium text-fg sm:col-start-auto sm:text-right">{issue.value}</span>
                <span className="col-start-2 flex items-center justify-between gap-2 font-mono text-xs text-fg-muted sm:col-start-auto">
                  {issue.since}
                  {showClient && <ChevronRight size={15} aria-hidden />}
                </span>
              </>
            );
            return (
              <li key={issue.key}>
                {showClient ? (
                  <Link to={`/clients/${issue.clientId}`} className={`${ROW} -mx-3 rounded-lg px-3 text-inherit no-underline hover:bg-hover`}>
                    {body}
                  </Link>
                ) : (
                  <div className={ROW}>{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
